<script setup lang="ts">
import KartNavigation from "../../../components/website/KartNavigation.vue";
import ImageHeader from "../../../components/website/ImageHeader.vue";
import Who from "../../../components/website/LayoutForWho.vue";
import { useTranslate } from "addeus-common-library/stores/translate";
import { breakpointsSematic, computedAsync, useBreakpoints } from "@vueuse/core";
import { useOwner } from "../../../stores/website/owner";
import functions from "addeus-common-library/stores/function";
import SpeedTranslate from "../../../components/website/SpeedTranslate.vue";
import VLabel from "addeus-common-library/components/base/form/VLabel.vue";
import { storeToRefs } from "pinia";
import { useSeoMeta } from "../../../composables/useSeoMeta";

const breakpoints = useBreakpoints(breakpointsSematic);
const isPhone = breakpoints.smaller("tablet");

const DEFAULT_RACEGROUP_TYPES = [
    {
        numberOfRaces: 1,
        prices: [],
        duration: "8min",
    },
    {
        numberOfRaces: 2,
        prices: [],
        duration: "8min",
    },
    {
        numberOfRaces: 4,
        prices: [],
        duration: "8min",
    },
];

const { setTranslateNamespace, translate } = useTranslate();
setTranslateNamespace(".kart.runs");

const ownerManager = useOwner();
const { speeds } = storeToRefs(useOwner());

const raceGroupTypes = computedAsync(async () => {
    const ownerID = ownerManager.owner?.$getID();

    if (ownerID === undefined) return DEFAULT_RACEGROUP_TYPES;

    const groupTypes = await functions.fetchRaceGroupTypesV2({ ownerID: ownerID });
    return groupTypes;
}, DEFAULT_RACEGROUP_TYPES);

function navigateBooking(router: any) {
    const toPush = ownerManager.owner?.bookableOnline
        ? "/booking"
        : {
              name: "owners-:owner",
              params: {
                  owner: ownerManager.owner?.$getID(),
              },
          };
    void router.push(toPush);
}

function getCategories(): string {
    const speeds: string[] = [];
    for (const speed of ownerManager.speeds ?? []) {
        if (speed.override === undefined) {
            speeds.push(
                translate(
                    `speed2max.speeds.${speed.speed.name}.label`,
                ).value.toLowerCase(),
            );
        } else if ((speed.override.maxAge ?? 0) === 0) {
            speeds.push(
                translate("speed2max.speeds.min.label", {
                    min: speed.override.minAge,
                }).value.toLowerCase(),
            );
        } else {
            speeds.push(
                translate("speed2max.speeds.range.label", {
                    min: speed.override.minAge,
                    max: speed.override.maxAge,
                }).value.toLowerCase(),
            );
        }
    }
    return speeds.reverse().join(" - ");
}
function overrideBySpeedId(speedId: string) {
    return speeds.value.find((s) => s.speed.$getID() === speedId);
}

useSeoMeta({
    title: "Sessions karting électrique chronométrées | Réservation en ligne",
    description:
        "Sessions de karting électrique indoor chronométrées : 1, 2 ou 4 runs. Accessible dès 7 ans, tous niveaux. Réservation en ligne, karting Clermont-Ferrand.",
    path: "/kart/runs",
    breadcrumbs: [
        { name: "Accueil", path: "/" },
        { name: "Faire du kart", path: "/kart" },
        { name: "Les Runs", path: "/kart/runs" },
    ],
    schemaOrg: [
        {
            "@context": "https://schema.org",
            "@type": "SportsActivityLocation",
            name: "Speed2Max - Sessions de karting",
            description:
                "Sessions de karting électrique chronométrées pour tous les niveaux.",
            url: "https://speed2max.com/kart/runs",
            sport: "Karting",
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
                {
                    "@type": "Question",
                    name: "Quel âge minimum pour faire du karting chez Speed2Max ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "Les sessions de karting Speed2Max sont accessibles dès 7 ans, avec des karts adaptés à chaque tranche d'âge.",
                    },
                },
                {
                    "@type": "Question",
                    name: "Combien de temps dure une session de karting ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "Une session de karting dure environ 8 minutes par run. Plusieurs formules sont disponibles avec 1, 2 ou 4 runs.",
                    },
                },
                {
                    "@type": "Question",
                    name: "Faut-il réserver pour faire du karting ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "La réservation en ligne est vivement conseillée pour garantir votre créneau. Vous pouvez réserver directement sur notre site.",
                    },
                },
            ],
        },
    ],
});
</script>

<template>
    <VFlex flex-direction="column" class="has-background-dark">
        <KartNavigation current="runs"></KartNavigation>
        <ImageHeader secondary>
            <template #image>
                <img
                    src="/images/kart/faireDuKart/FDK-runs-header.jpg"
                    alt="Session de karting chronométrée Speed2Max" />
            </template>
            <template #title>
                <span> <Translate path=".title"></Translate></span>
            </template>
            <template #subtitle>
                <span class="has-text-white">
                    <Translate path=".subtitleP1"></Translate></span
                >&nbsp;<span><Translate path=".subtitleP2"></Translate></span>
            </template>
            <template #icon>
                <img
                    src="/images/kart/icon-faire-du-kart.svg"
                    alt="Icône karting électrique" />
            </template>
        </ImageHeader>
        <Who>
            <template #title>
                <Translate path=".title"></Translate>
            </template>
            <template #content> <Translate path=".content1"></Translate><br /> </template>
            <template #moreInfos>
                <Translate
                    v-if="
                        ownerManager.owner === null ||
                        ownerManager.owner?.speedLimitations.length === 0
                    "
                    path=".moreInfos"></Translate>
                <Translate
                    v-else
                    path=".moreInfosCustom"
                    :values="{ categories: getCategories() }"></Translate>
            </template>
            <template #icon>
                <img src="/images/kart/track-icon.png" alt="Icône piste de karting" />
            </template>
            <template #titleP1>
                <Translate path=".titleP1"></Translate>
            </template>
            <template #titleP2>
                <Translate path=".titleP2"></Translate>
            </template>
        </Who>
        <VFlex v-if="raceGroupTypes.length > 0" flex-direction="column" class="px-6 py-5">
            <VFlex
                :flex-direction="isPhone ? 'column' : 'row'"
                :column-gap="'1rem'"
                justify-content="space-between"
                align-items="stretch"
                flex-wrap="wrap"
                row-gap="1rem">
                <VFlex
                    v-for="raceGroupType in raceGroupTypes"
                    :key="raceGroupType.numberOfRaces"
                    flex-direction="column"
                    class="is-flex is-align-self-stretch is-flex-shrink-4"
                    :row-gap="isPhone ? '0rem' : '1rem'"
                    :style="{ 'max-width': isPhone ? '100%' : '33%' }">
                    <VBlock class="is-clickable has-background-dark is-flex">
                        <TranslateNamespace :path="'.' + raceGroupType.numberOfRaces">
                            <VFlex
                                flex-direction="row"
                                class="p-4 has-border-radius has-border-primary">
                                <VFlex flex-direction="column" style="width: 100%">
                                    <object
                                        :data="`/images/booking/race/courses-byProduct-${
                                            raceGroupType.numberOfRaces
                                        }run-${raceGroupType.duration ?? ''}.svg`"
                                        :aria-label="raceGroupType.numberOfRaces"
                                        class="is-align-self-stretch"
                                        type="image/svg+xml"
                                        style="width: 180px; max-width: 100%">
                                        <img
                                            :src="`/images/booking/race/courses-byProduct-${raceGroupType.numberOfRaces}run.svg`"
                                            :alt="raceGroupType.numberOfRaces" />
                                    </object>
                                    <p
                                        class="is-size-5 has-text-white has-text-weight-semibold is-uppercase has-text-ellipsis">
                                        <Translate
                                            v-if="raceGroupTypes.length > 1"
                                            path=".title" />
                                        <Translate v-else path=".alt" />
                                    </p>
                                    <VDivider class="my-3"></VDivider>
                                    <VFlex
                                        v-for="price in raceGroupType.prices.sort(
                                            (price, price1) =>
                                                price.name.localeCompare(price1.name),
                                        )"
                                        :key="price.name"
                                        flex-direction="row"
                                        justify-content="space-between">
                                        <VLabel
                                            v-if="
                                                overrideBySpeedId(price.speed) !==
                                                undefined
                                            "
                                            class="has-text-white">
                                            <SpeedTranslate
                                                :speed="
                                                    overrideBySpeedId(price.speed)?.speed
                                                "
                                                :override="
                                                    overrideBySpeedId(price.speed)
                                                        ?.override
                                                "></SpeedTranslate>
                                        </VLabel>
                                        <VLabel
                                            v-if="
                                                overrideBySpeedId(price.speed) !==
                                                undefined
                                            "
                                            class="has-text-primary">
                                            <PriceView
                                                :with-currency="true"
                                                :precision="0"
                                                :price="price.price"
                                                :owner="ownerManager.owner"></PriceView>
                                        </VLabel>
                                    </VFlex>
                                    <VLabel
                                        v-if="raceGroupType.prices.length === 0"
                                        class="has-text-white">
                                        <Translate>.noPrice</Translate>
                                    </VLabel>
                                </VFlex>
                            </VFlex>
                        </TranslateNamespace>
                    </VBlock>
                    <VBlock
                        v-if="raceGroupType.numberOfRaces > 3"
                        :style="{
                            opacity: raceGroupType.numberOfRaces > 3 ? 1 : 0,
                        }"
                        class="has-background-dark">
                        <VFlex
                            class="p-4 is-align-self-stretch has-border-radius has-border-primary">
                            <img
                                class="is-align-self-stretch"
                                style="width: 180px; max-width: 100%"
                                :alt="raceGroupType.numberOfRaces"
                                :src="`/images/booking/race/courses-byProduct-${raceGroupType.numberOfRaces}run-FreeRaces.svg`" />
                        </VFlex>
                    </VBlock>
                    <!-- <img
                        class="is-align-self-stretch"
                        style="width: 180px; max-width: 100%"
                        :alt="raceGroupType.numberOfRaces"
                        :src="`/images/booking/race/courses-byProduct-${raceGroupType.numberOfRaces}run-FreeRaces.svg`" /> -->
                </VFlex>
            </VFlex>
        </VFlex>
        <VFlex flex-direction="column" align-items="flex-start" class="px-6 py-5 mb-5">
            <ButtonPrimary
                v-if="ownerManager.owner"
                color="primary"
                class="is-align-self-flex-start mt-none is-mohol is-size-6"
                style="font-family: mohol !important"
                @click="navigateBooking($router)">
                <!-- <VIcon icon="/images/kart/book-button-1.png"></VIcon> -->
                <span class="is-uppercase">
                    <Translate path=".book"></Translate>
                </span>
            </ButtonPrimary>
            <VFlex v-else :flex-direction="'column'">
                <p class="is-size-6 mb-4">
                    <Translate path=".infoChooseOwner"></Translate>
                </p>
                <ButtonPrimary
                    color="primary"
                    class="is-align-self-flex-start mt-none is-mohol is-size-6"
                    style="font-family: mohol !important"
                    @click="$router.push('/owners')">
                    <!-- <VIcon icon="/images/kart/book-button-1.png"></VIcon> -->
                    <span class="is-uppercase">
                        <Translate path=".selectOwner"></Translate>
                    </span>
                </ButtonPrimary>
            </VFlex>
        </VFlex>

        <Footer></Footer>
    </VFlex>
</template>

<style scoped lang="scss">
// A modifier et finaliser, j'ai pas trouvé la source du template du btn
.mt-none {
    margin-top: 0 !important;
}
</style>
